import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "convex/react";
import { CooperationRequestModal } from "../components/CooperationRequestModal";
import { convexApi } from "../convexApi";
import { cooperationRequestMutationArgs, labelForRequestStatus, labelForRequestType, transitionCooperationRequest } from "../lib/cooperation";
import type { CooperationRequest, CooperationRequestStatus, CooperationRequestType, LexData } from "../lib/types";

type Tab = "inbox" | "sent";

type CooperationPersistence = {
  createRequest?: (request: CooperationRequest) => Promise<unknown>;
  updateStatus?: (request: CooperationRequest, nextStatus: CooperationRequestStatus) => Promise<unknown>;
};

const inboxStatuses = ["sent", "viewed", "awaiting_conflict_check", "conflict_check_requested"];

export function CooperationPage({ data, mode = "demo" }: { data: LexData; mode?: "convex" | "demo" }) {
  if (mode === "convex") return <LiveCooperationPage data={data} />;
  return <CooperationPageContent data={data} />;
}

function LiveCooperationPage({ data }: { data: LexData }) {
  const createRequestMutation = useMutation(convexApi.cooperation.createRequest);
  const updateStatusMutation = useMutation(convexApi.cooperation.updateStatus);
  const persistence: CooperationPersistence = {
    createRequest: (request) => createRequestMutation(cooperationRequestMutationArgs(request) as never),
    updateStatus: (request, nextStatus) => updateStatusMutation({ cooperationRequestId: request.id as never, status: nextStatus, eventType: nextStatus === "declined" ? "declined" : "viewed", metadataJson: "{}" }),
  };
  return <CooperationPageContent data={data} persistence={persistence} />;
}

function CooperationPageContent({ data, persistence }: { data: LexData; persistence?: CooperationPersistence }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab: Tab = searchParams.get("tab") === "sent" ? "sent" : "inbox";
  const [tab, setTab] = useState<Tab>(initialTab);
  const [requests, setRequests] = useState(data.cooperationRequests);
  const [composeOpen, setComposeOpen] = useState(searchParams.get("compose") === "1");
  const [composeType, setComposeType] = useState<CooperationRequestType>((searchParams.get("type") as CooperationRequestType) || "ask_view");

  useEffect(() => {
    if (searchParams.get("compose") === "1") setComposeOpen(true);
  }, [searchParams]);

  const viewerFirmId = data.viewerFirm?.id;
  const inbox = useMemo(() => requests.filter((request) => request.targetFirmId === viewerFirmId && inboxStatuses.includes(request.status)), [requests, viewerFirmId]);
  const sent = useMemo(() => requests.filter((request) => request.requesterFirmId === viewerFirmId), [requests, viewerFirmId]);
  const visible = tab === "inbox" ? inbox : sent;

  const addRequest = (request: CooperationRequest) => {
    setRequests((current) => [request, ...current]);
    void persistence?.createRequest?.(request);
    setComposeOpen(false);
    setTab("sent");
  };

  const decline = (request: CooperationRequest) => {
    setRequests((current) => current.map((item) => (item.id === request.id ? transitionCooperationRequest(item, "declined") : item)));
    void persistence?.updateStatus?.(request, "declined");
  };

  const openRequest = (request: CooperationRequest) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    next.set("request", request.id);
    setSearchParams(next);
  };

  return (
    <>
      <div className="phead">
        <div className="eyebrow">Expertise exchange</div>
        <h1 className="h1 serif">Requests &amp; cooperation</h1>
        <p className="lead">Structured member-to-member requests with conflict-check safeguards built in. Share a non-confidential summary first; client names follow only when professionally appropriate.</p>
      </div>

      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px 0" }}>
          <div className="tabs" style={{ padding: 0 }}>
            <button type="button" className={`tab${tab === "inbox" ? " tab-on" : ""}`} onClick={() => setTab("inbox")}>To your firm · {inbox.length}</button>
            <button type="button" className={`tab${tab === "sent" ? " tab-on" : ""}`} onClick={() => setTab("sent")}>Sent by your firm · {sent.length}</button>
          </div>
          <button type="button" className="btn-sm btn-primary" style={{ marginRight: 4 }} onClick={() => setComposeOpen(true)}>New request</button>
        </div>
        <div style={{ height: 1, background: "var(--line)", margin: "12px 0 0" }} />
        {visible.length > 0 ? (
          visible.map((request) => <CoopRow key={request.id} request={request} data={data} tab={tab} onOpen={() => openRequest(request)} onDecline={() => decline(request)} />)
        ) : (
          <div className="rec"><div className="row-meta" style={{ margin: 0 }}>No requests in this view.</div></div>
        )}
      </div>

      {composeOpen ? (
        <CooperationRequestModal data={data} defaultRequestType={composeType} onClose={() => setComposeOpen(false)} onCreate={addRequest} />
      ) : null}
    </>
  );
}

function CoopRow({ request, data, tab, onOpen, onDecline }: { request: CooperationRequest; data: LexData; tab: Tab; onOpen: () => void; onDecline: () => void }) {
  const counterpartyFirmId = tab === "inbox" ? request.requesterFirmId : request.targetFirmId;
  const firm = data.firms.find((item) => item.id === counterpartyFirmId);
  const initials = (firm?.name ?? "ME").split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  const tone = statusTone(request.status);
  const dir = tab === "inbox" ? "From" : "To";

  return (
    <div className="coop" style={{ display: "flex", gap: 14, padding: "16px 20px", borderBottom: "1px solid var(--line)", alignItems: "flex-start" }}>
      <span className="mavatar" style={{ width: 40, height: 40, fontSize: 16 }}>{initials}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
          <span className={`pill ${tone}`}>{labelForRequestStatus(request.status)}</span>
          <span className="tag tag-accent">{labelForRequestType(request.requestType)}</span>
          <span className="row-meta" style={{ margin: 0 }}>{dir} {firm?.name ?? "Member firm"}{firm ? ` · ${firm.jurisdiction}` : ""} · {relativeAge(request.updatedAt)}</span>
        </div>
        <div className="row-title">{request.title}</div>
        <p className="lead" style={{ fontSize: 16, marginTop: 5, maxWidth: "none" }}>{request.shortSummary}</p>
      </div>
      <div className="mactions">
        <button type="button" className="btn-sm btn-primary" onClick={onOpen}>Open</button>
        {tab === "inbox" ? <button type="button" className="btn-sm" onClick={onDecline}>Decline</button> : null}
      </div>
    </div>
  );
}

function statusTone(status: string) {
  if (status === "awaiting_conflict_check" || status === "conflict_check_requested") return "pill-warn";
  if (status === "sent") return "pill-sky";
  if (status === "viewed") return "pill-neutral";
  if (["accepted", "conflict_cleared", "in_progress", "introduced", "completed"].includes(status)) return "pill-ok";
  if (["declined", "withdrawn", "expired", "closed"].includes(status)) return "pill-bad";
  return "pill-neutral";
}

function relativeAge(time: number) {
  const abs = Math.abs(time - Date.now());
  const hour = 60 * 60 * 1000;
  if (abs < hour) return "just now";
  if (abs < 24 * hour) return `${Math.max(1, Math.round(abs / hour))}h ago`;
  return `${Math.max(1, Math.round(abs / (24 * hour)))}d ago`;
}
